import AppContext from '../../common/context/AppContext';
import TvDetailsPanel from './tv/TvDetailsPanel';
import { RegulationEditorPanel } from './measure/RegulationEditorPanel';
import { SlotListPanel } from './measure/SlotListPanel';
import { InteractionStudyPanel } from './measure/InteractionStudyPanel';
import { QueryPanel } from './query/QueryPanel';
import { NetworkImprovementPanel } from './flights/NetworkImprovementPanel';
import SolverPanel from './solver/SolverPanel';
import React from 'react';
import { RegulationPeriods } from 'app/model/client/CORE';
import {
    CountType,
    DatasetDto,
    FlightDto,
    FlightId,
    LatLonPoint,
    MeasureId,
    RegulationDto,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import { ShowModalEvent } from 'app/common/eventbus/Events';
import { TVTimePeriodSelectionPanel } from './solver/TVTimePeriodSelectionPanel';
import {
    DetailedSolution,
    ExtendedMonitoredTv,
    MeteoForecast,
    MonitoredMcdmMeasure,
    NetworkMapConvection,
    PolygonMeteoForecast,
    RadRestrictions,
    TrafficVolumeMeteoForecast,
    TvMeteoAlert,
    TVTimePeriodSelection,
} from 'app/model/SOL49';
import { ConvectionDetailsContent } from '../dashboard/network/map/ConvectionDetails';
import { MapPanel } from './map/MapPanel';
import { SimulationPanel } from './simulation/SimulationPanel';
import FlightDetailsPanel from './flights/details/FlightDetailsPanel';
import FlightOpLogPanel from 'app/components/panels/flights/oplog/FlightOpLogPanel';
import MeasureOpLogPanel from 'app/components/panels/measure/oplog/MeasureOpLogPanel';
import { McdmMeasurePanel } from 'app/components/panels/coordination/McdmMeasurePanel';
import { MeasureProposalFilterSettings } from 'app/components/dashboard/detailed/measures/MeasureProposalFilter';
import { SettingsPanel } from './settings/SettingsPanel';
import { ShareSolutionPanel } from './solver/ShareSolutionPanel';
import { CoordinationPanel } from './coordination/CoordinationPanel';
import { ConvectionFlightListPanel } from 'app/components/panels/flights/ConvectionFlightListPanel';
import { Flex } from 'app/components/common/Flex';
import { MapLayer, SolutionTvImpact } from 'app/components/dashboard/network/map/Map';
import LayerContentPopup from 'app/components/dashboard/network/map/LayerContentPopup';
import { TvDetailsComparison } from 'app/components/panels/tv/details/TvDetailsComparison';
import ApplicableScenariosPanel from './scenario/ApplicableScenariosPanel';
import { ScenarioUtils } from './scenario/ScenarioUtils';
import { ApplicableRadRestrictionPanel } from './rad/ApplicableRadRestrictionPanel';

/**
 * Helper class for modal panels display
 */
export class ModalHandler {
    static showTvDetails(
        tvId: string,
        vsName: string,
        regulationPeriods: Array<RegulationPeriods>,
        occupancyPeriods: Map<FlightId, TimePeriod>,
    ) {
        const modalContent = (
            <TvDetailsPanel
                currentTime={AppContext.getCurrentTime()}
                tvId={tvId}
                tvSet={vsName}
                regulationPeriods={regulationPeriods}
                occupancyPeriods={occupancyPeriods}
            />
        );
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, TvDetailsPanel.INITIAL_WIDTH),
        );
    }

    static showTvImpactDetails(tvId: string, vsName: string, tvImpact: SolutionTvImpact) {
        const modalContent = (
            <TvDetailsComparison
                currentTime={AppContext.getCurrentTime()}
                timePeriod={tvImpact.timePeriod}
                tvId={tvId}
                tvSet={vsName}
                monitoredTvAfter={tvImpact.monitoredTv}
                monitoredTvBefore={tvImpact.monitoredTvBefore}
            />
        );
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, RegulationEditorPanel.INITIAL_WIDTH),
        );
    }

    static showRegulationEditor(
        tvId: string,
        idType: 'tv' | 'reg',
        initialRegulation?: Partial<RegulationDto>,
        readonly?: boolean,
    ) {
        const modalContent = (
            <RegulationEditorPanel
                currentTime={AppContext.getCurrentTime()}
                id={tvId}
                idType={idType}
                initialRegulation={initialRegulation}
                readonly={readonly}
            />
        );
        ShowModalEvent.fireNotClosable(modalContent, RegulationEditorPanel.INITIAL_WIDTH);
    }
    static showSlotList(measureId: MeasureId) {
        const modalContent = <SlotListPanel measureId={measureId} />;
        ShowModalEvent.fireNotClosable(modalContent, SlotListPanel.INITIAL_WIDTH);
    }
    static showInteractionStudy(measureId: MeasureId) {
        const modalContent = <InteractionStudyPanel measureId={measureId} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, InteractionStudyPanel.INITIAL_WIDTH),
        );
    }
    static showQueryPanel(initialQuery?: string) {
        const modalContent = <QueryPanel initialQuery={initialQuery} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, modalContent, QueryPanel.INITIAL_WIDTH));
    }
    static showFlightNetworkImprovement(flight: FlightDto) {
        const modalContent = <NetworkImprovementPanel flight={flight} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, NetworkImprovementPanel.INITIAL_WIDTH),
        );
    }

    static showFindSolution(tvs: Array<TVTimePeriodSelection>, proposedMeasures?: Array<MonitoredMcdmMeasure>) {
        const modalContent = <SolverPanel tvs={tvs} proposedMeasures={proposedMeasures} />;
        ShowModalEvent.fireNotClosable(modalContent, SolverPanel.INITIAL_WIDTH);
    }

    static showApplicableRadRestrictions(selectedRadId: string, radRestrictions: RadRestrictions) {
        const modalContent = (
            <ApplicableRadRestrictionPanel
                applicableRadRestrictions={radRestrictions}
                initialRadSelectionId={selectedRadId}
                initialTimePeriod={ScenarioUtils.getDefaultScenarioTimePeriod()}
            />
        );
        ShowModalEvent.fireNotClosable(modalContent, ApplicableRadRestrictionPanel.INITIAL_WIDTH_FULL);
    }

    static showRadRestrictionsByArea(selectedAreaPolygon?: Array<LatLonPoint>) {
        const modalContent = (
            <ApplicableRadRestrictionPanel
                selectedAreaPolygon={selectedAreaPolygon}
                initialTimePeriod={ScenarioUtils.getDefaultScenarioTimePeriod()}
            />
        );
        ShowModalEvent.fireNotClosable(modalContent, ApplicableRadRestrictionPanel.INITIAL_WIDTH_LIST);
    }

    static showAllApplicableScenarios() {
        const modalContent = (
            <ApplicableScenariosPanel initialTimePeriod={ScenarioUtils.getDefaultScenarioTimePeriod()} />
        );
        ShowModalEvent.fireNotClosable(modalContent, ApplicableScenariosPanel.INITIAL_WIDTH);
    }

    static showApplicableScenariosByArea(selectedAreaPolygon?: Array<LatLonPoint>) {
        const modalContent = (
            <ApplicableScenariosPanel
                selectedAreaPolygon={selectedAreaPolygon}
                initialTimePeriod={ScenarioUtils.getDefaultScenarioTimePeriod()}
            />
        );
        ShowModalEvent.fireNotClosable(modalContent, ApplicableScenariosPanel.INITIAL_WIDTH);
    }

    static showApplicableScenariosByTV(selectedTvs: string[], applicability: TimePeriod, selectedScenarioId: string) {
        const scenarioTimePeriod = applicability ? applicability : ScenarioUtils.getDefaultScenarioTimePeriod();
        const modalContent = (
            <ApplicableScenariosPanel
                selectedTvIds={selectedTvs}
                selectedScenarioId={selectedScenarioId}
                initialTimePeriod={scenarioTimePeriod}
            />
        );
        ShowModalEvent.fireNotClosable(modalContent, ApplicableScenariosPanel.INITIAL_WIDTH);
    }

    static showTVTimePeriodSelection(
        monitoredTvs: Array<ExtendedMonitoredTv>,
        tvSelection: Array<TVTimePeriodSelection>,
        tvMeteoAlerts: { [key: string]: Array<TvMeteoAlert> },
        onTvSelectionChange: (tvSelection: Array<TVTimePeriodSelection>) => void,
    ) {
        const modalContent = (
            <TVTimePeriodSelectionPanel
                monitoredTvs={monitoredTvs}
                defaultTvSelection={tvSelection}
                tvMeteoAlerts={tvMeteoAlerts}
                onTvSelectionChange={onTvSelectionChange}
            />
        );
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, TVTimePeriodSelectionPanel.INITIAL_WIDTH),
        );
    }
    static showMapConvectionDetails(meteoForecasts: MeteoForecast[], left: number, top: number) {
        const modalContent = <ConvectionDetailsContent convections={meteoForecasts} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, ConvectionDetailsContent.INITIAL_WIDTH, left, top),
        );
    }
    static showMapPanel(flights?: Array<FlightDto>, tvIds?: Array<string>, mapConvection?: NetworkMapConvection) {
        const modalContent = <MapPanel flights={flights} tvIds={tvIds} mapConvection={mapConvection} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, modalContent, MapPanel.INITIAL_WIDTH));
    }
    static showSimulationPanel() {
        const modalContent = <SimulationPanel />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, SimulationPanel.INITIAL_WIDTH),
        );
    }

    static showFlightDetailsPanel(id: FlightId, dataset: DatasetDto, proposalContext: string) {
        const modalContent = <FlightDetailsPanel id={id} dataset={dataset} proposalContext={proposalContext} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, FlightDetailsPanel.INITIAL_WIDTH),
        );
    }

    static showFlightOpLog(id: FlightId, dataset: DatasetDto) {
        const modalContent = <FlightOpLogPanel id={id} dataset={dataset} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, FlightOpLogPanel.INITIAL_WIDTH),
        );
    }

    static showMeasureOpLog(id: MeasureId) {
        const modalContent = <MeasureOpLogPanel id={id} />;
        AppContext.eventBus.publish(
            new ShowModalEvent(ShowModalEvent.NAME, modalContent, MeasureOpLogPanel.INITIAL_WIDTH),
        );
    }

    static showMcdmMeasures(
        monitoredMcdmMeasures: Array<MonitoredMcdmMeasure>,
        filterSettings: MeasureProposalFilterSettings,
    ) {
        const content = <McdmMeasurePanel proposals={monitoredMcdmMeasures} filterSettings={filterSettings} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '100em'));
    }
    static showSettingsPanel() {
        const modalContent = <SettingsPanel />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, modalContent, SettingsPanel.INITIAL_WIDTH));
    }
    static showShareSolutionPanel(
        solutions: DetailedSolution[],
        computedSolutions: DetailedSolution[],
        onShareSolution: () => void,
    ) {
        const content = (
            <ShareSolutionPanel
                solutions={solutions}
                computedSolutions={computedSolutions}
                onShareSolution={onShareSolution}
            />
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '40em'));
    }

    static showCoordinationPanel(coordinationId: string, onCoordinationUpdated: (Coordination) => void) {
        const content = (
            <CoordinationPanel coordinationId={coordinationId} onCoordinationUpdated={onCoordinationUpdated} />
        );
        ShowModalEvent.fireNotClosable(content, '130em');
    }

    static showConvectionImpactedFlights(convection: TrafficVolumeMeteoForecast) {
        const content = (
            <Flex style={{ minHeight: '60em', padding: '3em' }}>
                <ConvectionFlightListPanel
                    flightLevelBand={convection.flightLevelBand}
                    tvRefLocAirspaceId={convection.airspaceRef.id}
                    tvId={convection.id}
                    countType={CountType.ENTRY}
                    trafficWindow={convection.validityPeriod}
                    trafficType={TrafficType.LOAD}
                />
            </Flex>
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, ConvectionFlightListPanel.WIDTH));
    }

    static showPolygonConvectionImpactedFlightsForTv(convection: PolygonMeteoForecast, tv: string) {
        const content = (
            <Flex style={{ minHeight: '60em', padding: '3em' }}>
                <ConvectionFlightListPanel
                    flightLevelBand={convection.flightLevelBand}
                    tvRefLocAirspaceId={convection.impactedTvs[tv].id}
                    tvId={tv}
                    countType={CountType.ENTRY}
                    trafficWindow={convection.validityPeriod}
                    trafficType={TrafficType.LOAD}
                />
            </Flex>
        );
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, ConvectionFlightListPanel.WIDTH));
    }

    static showMapLayerContent(layer: MapLayer): void {
        const content = <LayerContentPopup objects={layer.objectsToDisplay} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '25em'));
    }
}
